import { useEffect } from "react";

// Would a vertical drag of `dy` px starting on `target` scroll something
// inside the locked overlay? True when some ancestor below <body> scrolls
// vertically and has room left in that direction; at its end the drag would
// chain to the page instead.
const scrollsInside = (target: EventTarget | null, dy: number): boolean => {
  let el: Element | null = target instanceof Element ? target : null;
  while (el && el !== document.body) {
    const current: Element = el;
    el = current.parentElement;
    if (
      !/auto|scroll/.test(window.getComputedStyle(current).overflowY) ||
      current.scrollHeight <= current.clientHeight
    )
      continue;
    const atTop = current.scrollTop <= 0;
    const atBottom =
      current.scrollTop + current.clientHeight >= current.scrollHeight - 1;
    return dy > 0 ? !atTop : !atBottom;
  }
  return false;
};

// Locks are counted, not stacked: only the first saves the page's overflow
// and only the last restores it. Each lock restoring what it found broke
// when locks nested (Add to list inside the work modal) and unmounted
// together on a back swipe: React cleans up the parent first, so the child
// restored the parent's "hidden" last and the page never scrolled again.
let locks = 0;
let saved = { html: "", body: "" };

// Lock page scrolling while the calling component is mounted (modals,
// sheets) without moving the page. `overflow: hidden` covers wheel and
// keyboard scrolling, but iOS Safari ignores it for touch, so vertical
// drags that would reach the page are cancelled here instead. An earlier
// version pinned <body> with position: fixed, which reset the page to the
// top and had to scroll it back on close — visible on iOS as the page
// re-scrolling whenever a modal closed.
export function useScrollLock(): void {
  useEffect(() => {
    const html = document.documentElement.style;
    const body = document.body.style;
    if (locks++ === 0) {
      saved = { html: html.overflow, body: body.overflow };
      html.overflow = "hidden";
      body.overflow = "hidden";
    }

    let startX = 0;
    let startY = 0;
    const onTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    const onTouchMove = (e: TouchEvent) => {
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      // Horizontal drags (swipe-to-close, sideways rows) are left alone.
      if (Math.abs(dx) > Math.abs(dy)) return;
      if (!scrollsInside(e.target, dy) && e.cancelable) e.preventDefault();
    };
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      if (--locks === 0) {
        html.overflow = saved.html;
        body.overflow = saved.body;
      }
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
    };
  }, []);
}
